import {
  GAF_PROGRAM,
  gafRewardsSquares,
  MATERIAL_ORDER_BASE_STATUS,
  partShortOrders,
  programYear,
  readyToRegister,
  todayCentral,
  type JobFileEntry,
  type PartShortOrder,
  type RegistrationDue,
} from "@roof-hawk/core";

import { supabase, unwrap } from "./supabase";
import {
  fetchProductParts,
  fetchWarranties,
  fetchWarrantyReceipts,
  JOB_COLUMNS,
  withCategories,
  type OrderLines,
  type WarrantyJob,
  type WarrantyRow,
} from "./warranties";

/**
 * Everything the Warranties page reads, in one call (ADR 0309).
 *
 * Four questions, four reads: what has been registered, which completed jobs
 * are still to be, which open orders are a part short, and how many GAF
 * squares went on this year's completed roofs. This file only reads the rows;
 * which of them each list shows is decided in `packages/core`
 * (`readyToRegister`, `partShortOrders`, `gafRewardsSquares`).
 */

/** A completed job whose warranty is still to be registered. */
export type ReadyToRegister = RegistrationDue<WarrantyJob>;

/** An open order short of the tier it could reach. */
export type AccessoryAlert = PartShortOrder<WarrantyJob>;

export interface WarrantyBoard {
  warranties: WarrantyRow[];
  receipts: Map<string, JobFileEntry>;
  ready: ReadyToRegister[];
  alerts: AccessoryAlert[];
  /** Squares of GAF shingle ordered for jobs completed in the GAF year. */
  rewardsSquares: number;
  today: string;
}

/** Everything the Warranties page shows, in one read. */
export async function fetchWarrantyBoard(): Promise<WarrantyBoard> {
  const today = todayCentral();
  const warranties = await fetchWarranties();
  const [receipts, pending, parts] = await Promise.all([
    fetchWarrantyReceipts(warranties),
    unwrap<{ job_id: string; job: WarrantyJob | null }[]>(
      await supabase
        .from("job_checklist_items")
        .select(
          `job_id, job:jobs!job_checklist_items_job_id_fkey(${JOB_COLUMNS})`,
        )
        .eq("label", "Register Warranty")
        .eq("status", "pending"),
    ),
    fetchProductParts(),
  ]);

  // Ready to register: the work is complete and the step is still open.
  const pendingIds = pending.map((p) => p.job_id);
  const [completes, estimates] =
    pendingIds.length === 0
      ? [[], []]
      : await Promise.all([
          unwrap<{ job_id: string; completed_at: string | null }[]>(
            await supabase
              .from("job_checklist_items")
              .select("job_id,completed_at")
              .in("job_id", pendingIds)
              .eq("label", "All Work Complete")
              .eq("status", "done"),
          ),
          unwrap<{ job_id: string; warranty: string | null }[]>(
            await supabase
              .from("estimates")
              .select("job_id,warranty")
              .in("job_id", pendingIds)
              .eq("is_active", true),
          ),
        ]);
  const tierByJob = new Map(estimates.map((e) => [e.job_id, e.warranty]));
  const ready = readyToRegister(
    pending.map((row) => ({
      job: row.job,
      completedAt:
        completes.find((c) => c.job_id === row.job_id)?.completed_at ?? null,
      estimateWarranty: tierByJob.get(row.job_id) ?? null,
    })),
  );

  // Orders not yet bought; partShortOrders decides which are worth listing.
  const openOrders = unwrap<
    (OrderLines & { order_number: number | null; job: WarrantyJob | null })[]
  >(
    await supabase
      .from("material_orders")
      .select(
        `id,job_id,status,order_number,created_at,items:material_order_items(id,product_id,name,color,qty,unit,unit_price),job:jobs!material_orders_job_id_fkey(${JOB_COLUMNS})`,
      )
      .eq("is_live", true)
      .eq("status", MATERIAL_ORDER_BASE_STATUS)
      .order("created_at", { ascending: false }),
  );
  const alerts = partShortOrders(
    openOrders.map((order) => ({
      ...order,
      lines: withCategories(order.items, parts),
    })),
  );

  // Rewards squares: the read is narrowed to the GAF year, and
  // gafRewardsSquares decides what counts.
  const year = programYear(GAF_PROGRAM.start, today);
  const completed = unwrap<{ job_id: string; completed_at: string }[]>(
    await supabase
      .from("job_checklist_items")
      .select("job_id,completed_at")
      .eq("label", "All Work Complete")
      .eq("status", "done")
      .gte("completed_at", `${year.from}T00:00:00`)
      .lt("completed_at", `${year.to}T00:00:00`),
  );
  const completedIds = completed.map((c) => c.job_id);
  let rewardsSquares = 0;
  if (completedIds.length > 0) {
    const orders = unwrap<OrderLines[]>(
      await supabase
        .from("material_orders")
        .select(
          "id,job_id,status,created_at,items:material_order_items(id,product_id,name,color,qty,unit,unit_price)",
        )
        .in("job_id", completedIds)
        .eq("is_live", true),
    );
    rewardsSquares = gafRewardsSquares(
      completed,
      orders.map((order) => ({
        job_id: order.job_id,
        lines: withCategories(order.items ?? [], parts),
      })),
      today,
    );
  }

  return { warranties, receipts, ready, alerts, rewardsSquares, today };
}
